import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getJson, sendJson } from '@/lib/api';

/**
 * Client types, fetchers, query hooks and pure helpers for the 13-week cash
 * forecast pages (Analytics → Accounting: Cash flow, Receivables, Assumptions,
 * Forecast vs actual). Mirrors the server contract in api/v3/cash/*. Dates
 * arrive as ISO strings; money is dollars.
 *
 * Cash lines are rendered from the arrays the API sends, never from a fixed
 * list: a line the server adds later (new bookings, say) shows up with a
 * readable label and its drill-down without a change here.
 */

// ─── Names ───────────────────────────────────────────────────────────────────

export const CASH_SCENARIO = {
  base: 'base',
  downside: 'downside',
} as const;
export type CashScenario = (typeof CASH_SCENARIO)[keyof typeof CASH_SCENARIO];

/**
 * Whether the gravy band was computed (server's GRAVY_MODE): the run-rate and
 * deals with no contract number yet, on top of the base case and never in its
 * closing cash. The downside case has none.
 */
export const GRAVY_MODE = {
  band: 'band',
  off: 'off',
} as const;
export type GravyMode = (typeof GRAVY_MODE)[keyof typeof GRAVY_MODE];

/** The gravy band's lines (server's GRAVY_LINE). */
export const GRAVY_LINE = {
  newBookings: 'new_bookings',
  earlierStage: 'earlier_stage',
} as const;

/** The base case's contract-stage line (server's IN_LINE.contractStage). */
export const CONTRACT_STAGE_LINE = 'contract_stage';

export const BALANCE_SOURCE = {
  manual: 'manual',
  quickbooks: 'quickbooks',
} as const;
export type BalanceSource = (typeof BALANCE_SOURCE)[keyof typeof BALANCE_SOURCE];

export const ACCOUNT_KIND = {
  bank: 'bank',
  creditCard: 'credit_card',
} as const;
export type AccountKind = (typeof ACCOUNT_KIND)[keyof typeof ACCOUNT_KIND];

export const ENTRY_DIRECTION = {
  in: 'in',
  out: 'out',
} as const;
export type EntryDirection = (typeof ENTRY_DIRECTION)[keyof typeof ENTRY_DIRECTION];

export const PARAMETER_SOURCE = {
  history: 'history',
  salesforce: 'salesforce',
  default: 'default',
  override: 'override',
} as const;
export type ParameterSource = (typeof PARAMETER_SOURCE)[keyof typeof PARAMETER_SOURCE];

export const NEEDS_CALL_REASON = {
  depositOverdue: 'deposit_overdue',
  balanceOverdue: 'balance_overdue',
  lowProbability: 'low_probability',
} as const;
export type NeedsCallReason = (typeof NEEDS_CALL_REASON)[keyof typeof NEEDS_CALL_REASON];

export const NEEDS_CALL_LABELS: Readonly<Record<NeedsCallReason, string>> = {
  [NEEDS_CALL_REASON.depositOverdue]: 'Deposit overdue',
  [NEEDS_CALL_REASON.balanceOverdue]: 'Balance overdue',
  [NEEDS_CALL_REASON.lowProbability]: 'Unlikely to pay',
};

/** Readable names for the lines the server sends today; anything else is humanized. */
const LINE_LABELS: Readonly<Record<string, string>> = {
  deposits: 'Deposits on signed events',
  balances: 'Balances on signed events',
  contract_stage: 'Contract-stage deals',
  manual_in: 'Other cash in',
  payroll: 'Payroll (excluding commissions)',
  commissions: 'Commissions (paid with payroll)',
  benefits: 'Benefits',
  open_bills: 'Open bills',
  event_costs: 'Event costs',
  overhead: 'Overhead',
  ads: 'Ad spend',
  manual_out: 'Other cash out',
};

/** "open_bills" → "Open bills": the fallback for a line this client does not know yet. */
export function humanize(key: string): string {
  const words = key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .toLowerCase();
  return words === '' ? key : words.charAt(0).toUpperCase() + words.slice(1);
}

const GRAVY_LABELS: Readonly<Record<string, string>> = {
  [GRAVY_LINE.newBookings]: 'New bookings (run rate)',
  [GRAVY_LINE.earlierStage]: 'Earlier-stage deals (weighted)',
};

/** The rates the base-case line labels quote. */
export interface LineRates {
  readonly contractToSigned?: number | undefined;
  readonly cancellation?: number | undefined;
}

/** Lines whose cash the cancellation rate cuts. */
const SIGNED_LINES: ReadonlySet<string> = new Set(['deposits', 'balances']);

/**
 * A cash line's label. With the measured rates the base-case lines say how
 * they were cut: "Contract-stage deals (97% expected)", "Balances on signed
 * events (less 0.7% cancellations)".
 */
export function lineLabel(line: string, rates: LineRates = {}): string {
  const base = LINE_LABELS[line] ?? humanize(line);
  if (line === CONTRACT_STAGE_LINE && rates.contractToSigned !== undefined) {
    return `${base} (${formatPercent(rates.contractToSigned)} expected)`;
  }
  if (SIGNED_LINES.has(line) && rates.cancellation !== undefined && rates.cancellation > 0) {
    return `${base} (less ${formatRate(rates.cancellation)} cancellations)`;
  }
  return base;
}

export function gravyLineLabel(line: string): string {
  return GRAVY_LABELS[line] ?? humanize(line);
}

// ─── Wire shapes ─────────────────────────────────────────────────────────────

export interface ForecastItem {
  readonly kind: string;
  readonly refId: string;
  readonly label: string;
  readonly date: string;
  readonly amount: number;
  readonly probability?: number;
  readonly fullAmount?: number;
}

export interface CashLine {
  readonly line: string;
  readonly amount: number;
  readonly items: readonly ForecastItem[];
}

export interface ForecastWeek {
  readonly index: number;
  readonly weekStart: string;
  /** First and last day the week covers; the first week starts today. */
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly opening: number;
  readonly cashIn: readonly CashLine[];
  readonly cashOut: readonly CashLine[];
  readonly totalIn: number;
  readonly totalOut: number;
  readonly closing: number;
  readonly belowMinimum: boolean;
  /** The gravy band's cash in by line, and the direct costs of the same bookings. */
  readonly gravyIn: readonly CashLine[];
  readonly gravyOut: readonly CashLine[];
  readonly gravyNet: number;
  readonly closingWithGravy: number;
}

export interface OpeningAccount {
  readonly accountId: string;
  readonly name: string;
  readonly kind: AccountKind;
  readonly source: BalanceSource;
  readonly amount: number;
  readonly contribution: number;
  readonly asOf: string | null;
  readonly missing: boolean;
}

export interface OpeningCash {
  readonly total: number;
  readonly accounts: readonly OpeningAccount[];
}

export interface Calibrated<T> {
  readonly value: T;
  readonly source: ParameterSource;
  readonly sampleSize: number;
  readonly window: { readonly from: string; readonly to: string } | null;
}

export interface CollectionCurve {
  readonly points: readonly { readonly offsetDays: number; readonly unpaidShare: number }[];
}

export interface CashFlowReport {
  readonly asOf: string;
  readonly scenario: CashScenario;
  readonly gravyMode: GravyMode;
  /** Whether the gravy band holds the new-bookings run rate (the Assumptions switch). */
  readonly gravyIncludesRunRate: boolean;
  readonly minimumCash: number;
  readonly opening: OpeningCash;
  readonly weeks: readonly ForecastWeek[];
  readonly totals: { readonly cashIn: number; readonly cashOut: number; readonly gravyIn: number; readonly gravyOut: number };
  readonly lowPoint: { readonly weekStart: string; readonly closing: number };
  readonly firstWeekBelowMinimum: string | null;
  readonly atRisk: { readonly amount: number; readonly items: readonly ForecastItem[] };
  readonly beyondHorizon: number;
  /** Calibrated values plus a few plain ones; read generically (see `describeParameters`). */
  readonly parameters: Readonly<Record<string, unknown>>;
  readonly overheadDecomposition: { readonly included: number; readonly excluded: Readonly<Record<string, number>> };
  /** Absent on an API older than this page. */
  readonly payroll?: PayrollBreakdown;
}

/** Where the payroll rate came from (server's PAYROLL_RATE_BASIS). */
export const PAYROLL_RATE_BASIS = {
  register: 'register',
  transfers: 'transfers',
  default: 'default',
} as const;
export type PayrollRateBasis = (typeof PAYROLL_RATE_BASIS)[keyof typeof PAYROLL_RATE_BASIS];

/** One payday: what BCE sent Team Building Alliance and the commissions paid with it. Totals only. */
export interface PaydayRow {
  readonly date: string;
  readonly transfer: number;
  readonly commissions: number;
  readonly commissionCount: number;
  readonly excludingCommissions: number;
  /** One of the runs the estimated payroll rate averages. */
  readonly setsRate: boolean;
}

export interface PayrollBreakdown {
  readonly paydays: readonly PaydayRow[];
  readonly rate: {
    readonly value: number;
    readonly basis: PayrollRateBasis;
    readonly note: string;
    readonly since: string | null;
    readonly sampleSize: number;
  };
  readonly commissions: Calibrated<number>;
  readonly benefits: Calibrated<number> & { readonly note: string };
}

export interface ReceivableRow {
  readonly opportunityId: string;
  readonly name: string;
  readonly accountName: string;
  readonly stageName: string;
  readonly signedAt: string | null;
  readonly eventDate: string | null;
  readonly paymentTerms: string;
  readonly contracted: number;
  readonly paid: number;
  readonly left: number;
  readonly depositOutstanding: number;
  readonly depositDueDate: string | null;
  readonly balanceOutstanding: number;
  readonly balanceDueDate: string | null;
  readonly daysLate: number;
  readonly collectionProbability: number;
  readonly expected: number;
  readonly needsCall: boolean;
  readonly needsCallReasons: readonly NeedsCallReason[];
}

export interface ReceivablesReport {
  readonly asOf: string;
  readonly totals: {
    readonly contracted: number;
    readonly paid: number;
    readonly left: number;
    readonly overdue: number;
    readonly expected: number;
    readonly atRisk: number;
    readonly needsCall: number;
  };
  readonly rows: readonly ReceivableRow[];
  readonly calibration: Calibrated<CollectionCurve>;
}

export interface ManualEntry {
  readonly id: string;
  readonly date: string;
  readonly amount: number;
  readonly direction: EntryDirection;
  readonly label: string;
}

export interface CashAccountConfig {
  readonly accountId: string;
  readonly name: string;
  readonly kind: AccountKind;
  readonly source: BalanceSource;
}

export interface BalanceEntry {
  readonly accountId: string;
  readonly amount: number;
  readonly asOf: string;
  readonly enteredAt: string;
  readonly enteredBy: string;
}

/** The payroll settings; null amounts and dates use what the forecast measures. */
export interface PayrollSettings {
  readonly perRunExcludingCommissions: number | null;
  readonly perRunSourceNote: string;
  readonly currentTeamSince: string | null;
  readonly commissionsPerPayday: number | null;
  readonly anchorDate: string | null;
}

/** Null monthly uses the default benefits amount. */
export interface BenefitsSettings {
  readonly monthly: number | null;
  readonly dayOfMonth: number;
}

export interface CashAssumptions {
  readonly minimumCash: number;
  readonly payroll: PayrollSettings;
  readonly benefits: BenefitsSettings;
  readonly adBudgetPerWeek: number | null;
  readonly eventCostRatio: number | null;
  /** Overrides of the measured rates; null uses the measurement. */
  readonly contractToSignedRate: number | null;
  readonly cancellationRate: number | null;
  readonly downsideHaircut: number;
  readonly manualEntries: readonly ManualEntry[];
  readonly excludedBillIds: readonly string[];
  readonly excludedVendorIds: readonly string[];
  /** The gravy band's run rate: `enabled` includes it, `baseFactor` scales it. `downsideFactor` is unused. */
  readonly newBookings: { readonly enabled: boolean; readonly baseFactor: number; readonly downsideFactor: number };
  readonly accounts: readonly CashAccountConfig[];
  readonly balanceEntries: readonly BalanceEntry[];
  /** Who is emailed the Monday summary and low-cash alerts (staff with Accounting access only). */
  readonly alertRecipients: readonly string[];
  readonly alertsEnabled: boolean;
  readonly updatedAt: string | null;
  readonly updatedBy: string;
}

export interface QuickBooksAccount {
  readonly accountId: string;
  readonly name: string;
  readonly accountType: string;
  readonly accountSubType: string;
  readonly active: boolean;
  readonly currentBalance: number;
  readonly asOf: string;
}

export interface OpenBill {
  readonly transactionId: string;
  readonly vendor: string;
  readonly vendorId: string;
  readonly billDate: string;
  readonly dueDate: string | null;
  readonly openBalance: number;
  readonly lineTotal: number;
}

export interface AssumptionsView {
  readonly assumptions: CashAssumptions;
  readonly opening: OpeningCash;
  readonly quickbooksAccounts: readonly QuickBooksAccount[];
  /** Absent on an API older than this page; treat as none. */
  readonly openBills?: readonly OpenBill[];
}

/** PUT /accounting/assumptions: the fields sent replace the stored ones. */
export interface AssumptionsUpdate {
  readonly minimumCash?: number;
  readonly payroll?: PayrollSettings;
  readonly benefits?: BenefitsSettings;
  readonly adBudgetPerWeek?: number | null;
  readonly eventCostRatio?: number | null;
  readonly contractToSignedRate?: number | null;
  readonly cancellationRate?: number | null;
  readonly downsideHaircut?: number;
  readonly manualEntries?: readonly {
    readonly id?: string;
    readonly date: string;
    readonly amount: number;
    readonly direction: EntryDirection;
    readonly label: string;
  }[];
  readonly excludedBillIds?: readonly string[];
  readonly newBookings?: { readonly enabled: boolean; readonly baseFactor: number; readonly downsideFactor: number };
  readonly accounts?: readonly CashAccountConfig[];
  readonly alertRecipients?: readonly string[];
  readonly alertsEnabled?: boolean;
}

export interface BankBalanceEntry {
  readonly accountId: string;
  readonly name?: string;
  readonly kind?: AccountKind;
  readonly amount: number;
  readonly asOf: string;
}

export interface HorizonAccuracy {
  readonly weeks: number;
  readonly forecastIn: number;
  readonly forecastOut: number;
  readonly forecastClosing: number;
  readonly actualIn: number;
  readonly actualOut: number;
  readonly actualClosing: number | null;
  readonly inVariance: number;
  readonly outVariance: number;
  readonly closingVariance: number | null;
  readonly complete: boolean;
}

export interface AccuracyRow {
  readonly snapshotId: string;
  readonly weekStart: string;
  readonly takenAt: string;
  readonly horizons: readonly HorizonAccuracy[];
}

export interface AccuracyReport {
  readonly asOf: string;
  readonly rows: readonly AccuracyRow[];
}

// ─── Fetchers + hooks ────────────────────────────────────────────────────────

export const CASH_QUERY_KEY = {
  all: ['accounting', 'cash'] as const,
  cashFlow: (scenario: CashScenario) => ['accounting', 'cash', 'flow', scenario] as const,
  receivables: ['accounting', 'cash', 'receivables'] as const,
  assumptions: ['accounting', 'cash', 'assumptions'] as const,
  accuracy: ['accounting', 'cash', 'accuracy'] as const,
} as const;

/**
 * The forecast: the base case, with the gravy band always computed beside it
 * (`gravyIn`, `closingWithGravy`), so the page shows or hides the band
 * without asking the server again.
 */
export function readCashFlow(options: { readonly scenario: CashScenario }, signal?: AbortSignal): Promise<CashFlowReport> {
  return getJson<CashFlowReport>('/accounting/cash-flow', { scenario: options.scenario }, signal);
}

export function readReceivables(signal?: AbortSignal): Promise<ReceivablesReport> {
  return getJson<ReceivablesReport>('/accounting/receivables', undefined, signal);
}

export function readAssumptions(signal?: AbortSignal): Promise<AssumptionsView> {
  return getJson<AssumptionsView>('/accounting/assumptions', undefined, signal);
}

export function readForecastAccuracy(signal?: AbortSignal): Promise<AccuracyReport> {
  return getJson<AccuracyReport>('/accounting/forecast-accuracy', undefined, signal);
}

export function useCashFlow(scenario: CashScenario) {
  return useQuery({
    queryKey: CASH_QUERY_KEY.cashFlow(scenario),
    queryFn: ({ signal }) => readCashFlow({ scenario }, signal),
    placeholderData: keepPreviousData,
  });
}

export function useReceivables() {
  return useQuery({ queryKey: CASH_QUERY_KEY.receivables, queryFn: ({ signal }) => readReceivables(signal) });
}

export function useAssumptions() {
  return useQuery({ queryKey: CASH_QUERY_KEY.assumptions, queryFn: ({ signal }) => readAssumptions(signal) });
}

export function useForecastAccuracy() {
  return useQuery({ queryKey: CASH_QUERY_KEY.accuracy, queryFn: ({ signal }) => readForecastAccuracy(signal) });
}

/** Saving assumptions or balances changes every forecast: refresh them all. */
function useCashWrite<TBody>(write: (body: TBody) => Promise<AssumptionsView>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: write,
    onSuccess: async (view) => {
      queryClient.setQueryData(CASH_QUERY_KEY.assumptions, view);
      await queryClient.invalidateQueries({ queryKey: CASH_QUERY_KEY.all });
    },
  });
}

export function useSaveAssumptions() {
  return useCashWrite((body: AssumptionsUpdate) => sendJson<AssumptionsView>('put', '/accounting/assumptions', body));
}

export function useSaveBankBalances() {
  return useCashWrite((entries: readonly BankBalanceEntry[]) =>
    sendJson<AssumptionsView>('post', '/accounting/bank-balances', { entries }),
  );
}

// ─── Formatting ──────────────────────────────────────────────────────────────

/** Same formatters as Expenses and P&L: whole dollars, and a compact form for axes and tight cells. */
const moneyFull = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const moneyCompact = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumFractionDigits: 1,
});

export function formatMoney(value: number): string {
  return moneyFull.format(value);
}

export function formatMoneyCompact(value: number): string {
  return moneyCompact.format(value);
}

export function formatPercent(share: number): string {
  return `${Math.round(share * 100)}%`;
}

/** A rate to a tenth of a percent: 0.0072 → "0.7%", 0.978 → "97.8%". */
export function formatRate(share: number): string {
  return `${(Math.round(share * 1000) / 10).toLocaleString('en-US', { maximumFractionDigits: 1 })}%`;
}

/** "2026-10-05T00:00:00.000Z" → "Oct 5" (UTC: the API's dates are UTC midnights). */
export function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

/** "2026-10-05T…" → "Oct 5, 2026". */
export function longDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

/** The `yyyy-mm-dd` part of an ISO date, for the DatePicker and the API. */
export function ymd(iso: string): string {
  return iso.slice(0, 10);
}

export function todayYmd(now: Date = new Date()): string {
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 10);
}

/** The days a week covers: "Oct 5–11", or "Sep 28–Oct 4" across a month. */
export function periodLabel(week: Pick<ForecastWeek, 'periodStart' | 'periodEnd'>): string {
  const start = new Date(week.periodStart);
  const end = new Date(week.periodEnd);
  const endText =
    start.getUTCMonth() === end.getUTCMonth() ? String(end.getUTCDate()) : shortDate(week.periodEnd);
  return `${shortDate(week.periodStart)}–${endText}`;
}

/** Week column header: "Week of Oct 5". */
export function weekLabel(weekStart: string): string {
  return `Wk of ${shortDate(weekStart)}`;
}

// ─── Week table ──────────────────────────────────────────────────────────────

export const WEEK_ROW_KIND = {
  section: 'section',
  line: 'line',
  total: 'total',
  net: 'net',
  balance: 'balance',
  /** The gravy band's rows: muted, never part of the closing balance. */
  gravy: 'gravy',
} as const;
export type WeekRowKind = (typeof WEEK_ROW_KIND)[keyof typeof WEEK_ROW_KIND];

export const FLOW_DIRECTION = {
  in: 'in',
  out: 'out',
} as const;
export type FlowDirection = (typeof FLOW_DIRECTION)[keyof typeof FLOW_DIRECTION];

export interface WeekCell {
  readonly amount: number;
  /** What is behind the number: events, bills, payroll runs. Empty when it is not a sum of items. */
  readonly items: readonly ForecastItem[];
}

export interface WeekRow {
  readonly key: string;
  readonly kind: WeekRowKind;
  readonly label: string;
  readonly direction?: FlowDirection;
  /** One per week; empty for a section heading. */
  readonly cells: readonly WeekCell[];
}

/** Line keys in the order they first appear across the weeks. */
function lineKeys(weeks: readonly ForecastWeek[], pick: (week: ForecastWeek) => readonly CashLine[]): string[] {
  const keys: string[] = [];
  for (const week of weeks) {
    for (const line of pick(week)) if (!keys.includes(line.line)) keys.push(line.line);
  }
  return keys;
}

function lineRows(
  weeks: readonly ForecastWeek[],
  direction: FlowDirection,
  pick: (week: ForecastWeek) => readonly CashLine[],
  shape: { readonly prefix: string; readonly kind: WeekRowKind; readonly label: (line: string) => string },
): WeekRow[] {
  return lineKeys(weeks, pick).map((key) => ({
    key: `${shape.prefix}:${key}`,
    kind: shape.kind,
    label: shape.label(key),
    direction,
    cells: weeks.map((week) => {
      const line = pick(week).find((candidate) => candidate.line === key);
      return { amount: line?.amount ?? 0, items: line?.items ?? [] };
    }),
  }));
}

const plain = (amount: number): WeekCell => ({ amount, items: [] });

/**
 * Shapes the forecast into the week table: opening, each cash-in line, total
 * in, each cash-out line, total out, net, closing, and (when asked) the gravy
 * band beneath: its cash in by line, the direct costs of those bookings, and
 * closing with gravy. Lines come from the data, in the order the server sends
 * them; a line with nothing in any week is dropped.
 *
 * @param weeks - The forecast weeks.
 * @param options.showGravy - Adds the gravy rows (never when the band is off).
 * @param options.gravyMode - `off` in the downside case.
 * @param options.rates - The measured rates the base-case labels quote.
 * @returns Table rows, one cell per week.
 */
export function buildWeekRows(
  weeks: readonly ForecastWeek[],
  options: { readonly showGravy: boolean; readonly gravyMode?: GravyMode; readonly rates?: LineRates },
): WeekRow[] {
  const nonEmpty = (row: WeekRow): boolean => row.cells.some((cell) => cell.amount !== 0);
  const baseShape = (prefix: string) => ({
    prefix,
    kind: WEEK_ROW_KIND.line,
    label: (line: string) => lineLabel(line, options.rates),
  });
  const rows: WeekRow[] = [
    { key: 'opening', kind: WEEK_ROW_KIND.balance, label: 'Opening cash', cells: weeks.map((w) => plain(w.opening)) },
    { key: 'section:in', kind: WEEK_ROW_KIND.section, label: 'Cash in', cells: [] },
    ...lineRows(weeks, FLOW_DIRECTION.in, (week) => week.cashIn, baseShape(FLOW_DIRECTION.in)).filter(nonEmpty),
    {
      key: 'total:in',
      kind: WEEK_ROW_KIND.total,
      label: 'Total cash in',
      direction: FLOW_DIRECTION.in,
      cells: weeks.map((w) => plain(w.totalIn)),
    },
    { key: 'section:out', kind: WEEK_ROW_KIND.section, label: 'Cash out', cells: [] },
    ...lineRows(weeks, FLOW_DIRECTION.out, (week) => week.cashOut, baseShape(FLOW_DIRECTION.out)).filter(nonEmpty),
    {
      key: 'total:out',
      kind: WEEK_ROW_KIND.total,
      label: 'Total cash out',
      direction: FLOW_DIRECTION.out,
      cells: weeks.map((w) => plain(w.totalOut)),
    },
    { key: 'net', kind: WEEK_ROW_KIND.net, label: 'Net cash flow', cells: weeks.map((w) => plain(w.totalIn - w.totalOut)) },
    { key: 'closing', kind: WEEK_ROW_KIND.balance, label: 'Closing cash', cells: weeks.map((w) => plain(w.closing)) },
  ];
  if (!options.showGravy || (options.gravyMode ?? GRAVY_MODE.band) === GRAVY_MODE.off) return rows;
  return [
    ...rows,
    { key: 'section:gravy', kind: WEEK_ROW_KIND.section, label: 'Expected bookings (not locked in)', cells: [] },
    ...lineRows(weeks, FLOW_DIRECTION.in, (week) => week.gravyIn, {
      prefix: 'gravy:in',
      kind: WEEK_ROW_KIND.gravy,
      label: gravyLineLabel,
    }).filter(nonEmpty),
    ...lineRows(weeks, FLOW_DIRECTION.out, (week) => week.gravyOut, {
      prefix: 'gravy:out',
      kind: WEEK_ROW_KIND.gravy,
      label: (line) => `Event costs of ${gravyLineLabel(line).toLowerCase()}`,
    }).filter(nonEmpty),
    { key: 'gravy:closing', kind: WEEK_ROW_KIND.gravy, label: 'Closing with expected bookings', cells: weeks.map((w) => plain(w.closingWithGravy)) },
  ];
}

/** The measured rates the labels quote, read off the forecast's parameters. */
export function lineRates(parameters: Readonly<Record<string, unknown>>): LineRates {
  return {
    contractToSigned: calibratedNumber(parameters, 'contractToSigned')?.value,
    cancellation: calibratedNumber(parameters, 'cancellationRate')?.value,
  };
}

// ─── Chart ───────────────────────────────────────────────────────────────────

export interface CashChartData {
  readonly points: readonly { key: string; label: string; value: number; details: string[] }[];
  readonly cashIn: readonly number[];
  /** Negative, so the out bars hang below zero on the same scale as the balance. */
  readonly cashOut: readonly number[];
  readonly closing: readonly number[];
  readonly minimum: readonly number[];
  readonly closingWithGravy: readonly number[];
}

/** The chart's series: weekly in/out bars, the closing line, the minimum line, the closing-with-gravy line. */
export function buildChartData(report: Pick<CashFlowReport, 'weeks' | 'minimumCash'>): CashChartData {
  return {
    points: report.weeks.map((week) => ({
      key: ymd(week.weekStart),
      label: periodLabel(week),
      value: week.closing,
      details: week.belowMinimum ? ['Below minimum cash'] : [],
    })),
    cashIn: report.weeks.map((week) => week.totalIn),
    cashOut: report.weeks.map((week) => -week.totalOut),
    closing: report.weeks.map((week) => week.closing),
    minimum: report.weeks.map(() => report.minimumCash),
    closingWithGravy: report.weeks.map((week) => week.closingWithGravy),
  };
}

// ─── Parameters ("How this is calculated") ──────────────────────────────────

/** How each known parameter reads; unknown ones fall back to a humanized key. */
interface ParameterMeta {
  readonly label: string;
  readonly unit: string;
  readonly format: ParameterFormat;
  /** Wording that replaces the generic basis for a source, where the generic one would mislead. */
  readonly basis?: Partial<Record<ParameterSource, (sampleSize: number) => string>>;
}

const PARAMETER_META: Readonly<Record<string, ParameterMeta>> = {
  collectionCurve: { label: 'Collection curve', unit: 'events', format: 'curve' },
  eventCostRatio: { label: 'Event cost ratio', unit: 'events', format: 'share' },
  eventCostSpreadWeeks: { label: 'Event costs paid over', unit: 'events', format: 'weeks' },
  overheadWeekly: { label: 'Overhead per week', unit: 'weeks', format: 'money' },
  adSpendWeekly: { label: 'Ad spend per week', unit: 'weeks', format: 'money' },
  payrollPerRun: {
    label: 'Payroll per run, excluding commissions',
    unit: 'runs',
    format: 'money',
    basis: {
      [PARAMETER_SOURCE.history]: (n) => `Estimated from transfers less commissions, ${n} ${n === 1 ? 'run' : 'runs'}`,
      [PARAMETER_SOURCE.override]: () => 'Set from the payroll register',
    },
  },
  commissionsPerPayday: { label: 'Commissions per payday', unit: 'paydays', format: 'money' },
  benefitsMonthly: {
    label: 'Benefits per month',
    unit: 'months',
    format: 'money',
    basis: { [PARAMETER_SOURCE.default]: () => `Default: ${DEFAULT_BENEFITS_NOTE}` },
  },
  payrollAnchor: { label: 'Last payroll run', unit: 'runs', format: 'date' },
  pipelineProbabilities: { label: 'Earlier-stage signing odds (expected bookings)', unit: 'deals', format: 'stages' },
  newBookingsWeekly: { label: 'New bookings per week (expected bookings)', unit: 'bookings', format: 'money' },
  newBookingsFactor: { label: 'New bookings factor (expected bookings)', unit: 'weeks', format: 'share' },
  contractToSigned: { label: 'Contract to signed', unit: 'deals over 2 years', format: 'rate' },
  cancellationRate: { label: 'Canceled after signing', unit: 'signed deals over 2 years', format: 'rate' },
  contractToSignDays: { label: 'Contract out to signed', unit: 'signed deals over 2 years', format: 'days' },
};
type ParameterFormat = 'money' | 'share' | 'rate' | 'weeks' | 'days' | 'date' | 'curve' | 'stages' | 'number';

export interface ParameterRow {
  readonly key: string;
  readonly label: string;
  readonly value: string;
  readonly source: ParameterSource;
  /** "Measured from 212 events", "Default (not enough history)", "Set on Assumptions". */
  readonly basis: string;
}

function isCalibrated(value: unknown): value is Calibrated<unknown> {
  return (
    typeof value === 'object' &&
    value !== null &&
    'value' in value &&
    'source' in value &&
    'sampleSize' in value &&
    typeof value.sampleSize === 'number' &&
    typeof value.source === 'string'
  );
}

function isCurve(value: unknown): value is CollectionCurve {
  return typeof value === 'object' && value !== null && 'points' in value && Array.isArray(value.points);
}

/** The share collected by the due date, read off the curve's survival points. */
export function collectedByDueDate(curve: CollectionCurve): number | null {
  const atDue = [...curve.points].filter((point) => point.offsetDays <= 0).pop();
  return atDue === undefined ? null : 1 - atDue.unpaidShare;
}

function formatParameterValue(value: unknown, format: ParameterFormat): string {
  if (format === 'curve' && isCurve(value)) {
    const collected = collectedByDueDate(value);
    const never = value.points[value.points.length - 1];
    const parts = [
      collected === null ? null : `${formatPercent(collected)} paid by the due date`,
      never === undefined ? null : `${formatPercent(never.unpaidShare)} never paid`,
    ].filter((part): part is string => part !== null);
    return parts.join(', ');
  }
  if (format === 'stages' && typeof value === 'object' && value !== null) {
    return Object.entries(value)
      .filter((entry): entry is [string, number] => typeof entry[1] === 'number')
      .map(([stage, odds]) => `${stage} ${formatPercent(odds)}`)
      .join(', ');
  }
  if (typeof value === 'number') {
    if (format === 'money') return formatMoney(value);
    if (format === 'share') return formatPercent(value);
    if (format === 'rate') return formatRate(value);
    if (format === 'days') return `${value} ${value === 1 ? 'day' : 'days'} (median)`;
    if (format === 'weeks') return `${value} ${value === 1 ? 'week' : 'weeks'}`;
    return value.toLocaleString('en-US');
  }
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) return longDate(value);
  return typeof value === 'string' ? value : '';
}

function basisFor(calibrated: Calibrated<unknown>, unit: string, meta?: ParameterMeta): string {
  const special = meta?.basis?.[calibrated.source];
  if (special) return special(calibrated.sampleSize);
  switch (calibrated.source) {
    case PARAMETER_SOURCE.history:
      return `Measured from ${calibrated.sampleSize.toLocaleString('en-US')} ${unit}`;
    case PARAMETER_SOURCE.salesforce:
      return 'Salesforce stage probability';
    case PARAMETER_SOURCE.override:
      return 'Set on the Assumptions page';
    case PARAMETER_SOURCE.default:
      return calibrated.sampleSize > 0
        ? `Default: only ${calibrated.sampleSize.toLocaleString('en-US')} ${unit} of history`
        : 'Default: no history yet';
    default: {
      const unknownSource: never = calibrated.source;
      return humanize(String(unknownSource));
    }
  }
}

/**
 * Lists every calibrated parameter the forecast used, with its value and
 * where it came from. Reads the parameters generically, so one the server
 * adds later is listed too.
 */
export function describeParameters(parameters: Readonly<Record<string, unknown>>): ParameterRow[] {
  return Object.entries(parameters).flatMap(([key, raw]): ParameterRow[] => {
    if (!isCalibrated(raw)) return [];
    const meta = PARAMETER_META[key] ?? { label: humanize(key), unit: 'data points', format: 'number' as const };
    return [
      {
        key,
        label: meta.label,
        value: formatParameterValue(raw.value, meta.format),
        source: raw.source,
        basis: basisFor(raw, meta.unit, meta),
      },
    ];
  });
}

// ─── What's included ─────────────────────────────────────────────────────────

/** The source the server names for the default benefits amount (payroll.ts DEFAULT_BENEFITS_SOURCE_NOTE). */
export const DEFAULT_BENEFITS_NOTE = 'TBA QuickBooks, Jun–Sep 2026 average';

/** What a cash line holds and where its numbers come from, in the reader's words. */
export interface IncludedLine {
  readonly key: string;
  readonly label: string;
  readonly includes: string;
  readonly source: string;
}

/** "Measured from 6 paydays", "Set on the Assumptions page", or the default's wording. */
function sourceText(calibrated: Calibrated<number> | undefined, unit: string, measuredFrom: string): string {
  if (!calibrated) return measuredFrom;
  if (calibrated.source === PARAMETER_SOURCE.override) return 'Set on the Assumptions page';
  if (calibrated.source === PARAMETER_SOURCE.default) return 'Default: not enough history yet';
  return `${measuredFrom}, ${calibrated.sampleSize.toLocaleString('en-US')} ${unit}`;
}

/** The payroll rate's source in words: the register (with its note), the transfers, or the default. */
export function payrollRateSource(rate: PayrollBreakdown['rate']): string {
  switch (rate.basis) {
    case PAYROLL_RATE_BASIS.register:
      return rate.note.trim() === '' ? 'Set from the payroll register' : `Set from the payroll register: ${rate.note.trim()}`;
    case PAYROLL_RATE_BASIS.transfers: {
      const runs = `${rate.sampleSize} ${rate.sampleSize === 1 ? 'run' : 'runs'}`;
      return rate.since
        ? `Estimated from transfers less commissions, ${runs} since ${longDate(rate.since)}`
        : `Estimated from transfers less commissions, the latest ${runs}`;
    }
    case PAYROLL_RATE_BASIS.default:
      return 'Default: no payroll runs found yet';
    default: {
      const unknownBasis: never = rate.basis;
      return humanize(String(unknownBasis));
    }
  }
}

/** The benefits amount's source in words. */
export function benefitsSource(benefits: PayrollBreakdown['benefits']): string {
  if (benefits.source === PARAMETER_SOURCE.override) return 'Set on the Assumptions page';
  return `Default: ${benefits.note.trim() === '' ? DEFAULT_BENEFITS_NOTE : benefits.note.trim()}`;
}

/**
 * One line per cash line saying what it holds and where its numbers come
 * from, for the "What's included" panel. Numbers are read off the report;
 * a parameter the API did not send is described without its number.
 */
export function describeIncludedLines(report: CashFlowReport): IncludedLine[] {
  const p = report.parameters;
  const cancellation = calibratedNumber(p, 'cancellationRate');
  const contract = calibratedNumber(p, 'contractToSigned');
  const costRatio = calibratedNumber(p, 'eventCostRatio');
  const overhead = calibratedNumber(p, 'overheadWeekly');
  const ads = calibratedNumber(p, 'adSpendWeekly');
  const commissions = report.payroll?.commissions ?? calibratedNumber(p, 'commissionsPerPayday');
  const payrollParameter = calibratedNumber(p, 'payrollPerRun');
  const payrollRate = report.payroll?.rate ?? payrollParameter;
  const benefits = report.payroll?.benefits;
  const benefitsDay = typeof p.benefitsDayOfMonth === 'number' ? p.benefitsDayOfMonth : undefined;
  const excluded = Object.values(report.overheadDecomposition.excluded).reduce((sum, value) => sum + value, 0);
  const cut = cancellation && cancellation.value > 0 ? `, less ${formatRate(cancellation.value)} for cancellations on events not held yet` : '';
  const lines: IncludedLine[] = [
    {
      key: 'deposits',
      label: lineLabel('deposits'),
      includes: `Unpaid deposits on signed events, timed by when BCE's clients actually pay${cut}.`,
      source: 'Salesforce signed events and payments received, plus Stripe',
    },
    {
      key: 'balances',
      label: lineLabel('balances'),
      includes: `Unpaid balances on signed events around their due dates, timed the same way${cut}.`,
      source: 'Salesforce signed events and payments received, plus Stripe',
    },
    {
      key: CONTRACT_STAGE_LINE,
      label: lineLabel(CONTRACT_STAGE_LINE),
      includes: `Deals with a contract out but not signed yet${contract ? `, counted at ${formatRate(contract.value)}` : ''}: the deposit when they sign and the balance when due.`,
      source: sourceText(contract, 'deals over 2 years', 'Salesforce open deals with a contract number; rate measured'),
    },
    {
      key: 'open_bills',
      label: lineLabel('open_bills'),
      includes: 'Unpaid QuickBooks bills on their due dates; overdue bills in the first week. Disputed vendors and bills excluded on the Assumptions page are left out.',
      source: 'QuickBooks open bills',
    },
    {
      key: 'event_costs',
      label: lineLabel('event_costs'),
      includes: `Facilitators, materials, shipping and travel for each event${costRatio ? `: ${formatPercent(costRatio.value)} of its contract value` : ''}, paid in the weeks after the event, less what is already billed.`,
      source: sourceText(costRatio, 'events', 'Measured from QuickBooks event spend against events held'),
    },
    {
      key: 'overhead',
      label: lineLabel('overhead'),
      includes: `Everything else bought from a bank account or card${overhead ? `: ${formatMoney(overhead.value)} a week` : ''}, built from ${formatMoney(report.overheadDecomposition.included)} of recent spend. ${formatMoney(excluded)} was left out because payroll, bills, event costs, ads and transfers have their own lines.`,
      source: sourceText(overhead, 'weeks', 'QuickBooks purchases, trimmed weekly average'),
    },
    {
      key: 'ads',
      label: lineLabel('ads'),
      includes: `The ad budget${ads ? `: ${formatMoney(ads.value)} a week` : ''}.`,
      source: sourceText(ads, 'weeks', 'QuickBooks ad spend, weekly average'),
    },
    {
      key: 'payroll',
      label: lineLabel('payroll'),
      includes: `Wages and payroll taxes for each run, every other Wednesday${payrollRate ? `: ${formatMoney(payrollRate.value)}` : ''}. Mid-cycle top-ups to Team Building Alliance are buffer and are not forecast.`,
      source: report.payroll ? payrollRateSource(report.payroll.rate) : sourceText(payrollParameter, 'runs', 'Estimated from transfers less commissions'),
    },
    {
      key: 'commissions',
      label: lineLabel('commissions'),
      includes: `Commissions paid to salespeople through payroll on each payday${commissions ? `: ${formatMoney(commissions.value)}` : ''}. They are taken out of the payroll line, so they count once.`,
      source: sourceText(commissions, 'paydays averaged', 'Salesforce commission payments'),
    },
    {
      key: 'benefits',
      label: lineLabel('benefits'),
      includes: `Group health, dental and the SIMPLE IRA, paid by Team Building Alliance monthly outside the payroll runs${benefits ? `: ${formatMoney(benefits.value)}` : ''}${benefitsDay ? ` around day ${benefitsDay}` : ''}.`,
      source: benefits ? benefitsSource(benefits) : 'Assumptions page',
    },
    {
      key: 'manual',
      label: 'Other cash in and out',
      includes: 'One-off amounts entered on the Assumptions page, on their dates.',
      source: 'Assumptions page',
    },
    {
      key: 'expected',
      label: 'Expected bookings',
      includes: 'Not locked in: new bookings at the recent signing rate and deals with no contract out yet. Shown on top of the closing cash, never in it or in the low-cash emails.',
      source: 'Salesforce signings and open deals',
    },
  ];
  return lines;
}

// ─── Receivables ─────────────────────────────────────────────────────────────

/** The receivables row, flattened for the table's sort and search. */
export interface ReceivableTableRow extends ReceivableRow {
  /** The earliest unpaid due date (deposit or balance), ISO; null when none is set. */
  readonly nextDueDate: string | null;
  readonly needsCallText: string;
}

export function nextDueDate(row: Pick<ReceivableRow, 'depositOutstanding' | 'depositDueDate' | 'balanceOutstanding' | 'balanceDueDate'>): string | null {
  const dates = [
    row.depositOutstanding > 0 ? row.depositDueDate : null,
    row.balanceOutstanding > 0 ? row.balanceDueDate : null,
  ].filter((date): date is string => date !== null);
  return dates.sort()[0] ?? null;
}

export function toReceivableTableRows(rows: readonly ReceivableRow[]): ReceivableTableRow[] {
  return rows.map((row) => ({
    ...row,
    nextDueDate: nextDueDate(row),
    needsCallText: row.needsCallReasons.map((reason) => NEEDS_CALL_LABELS[reason] ?? humanize(reason)).join(', '),
  }));
}

// ─── Assumptions ─────────────────────────────────────────────────────────────

/** Open bills already past their due date, oldest first: the stale ones to review. */
export function overdueBills(bills: readonly OpenBill[], today: string): OpenBill[] {
  return bills
    .filter((bill) => bill.dueDate !== null && ymd(bill.dueDate) < today)
    .sort((a, b) => (a.dueDate ?? '').localeCompare(b.dueDate ?? ''));
}

/** The newest hand-entered balance for an account, or undefined. */
export function latestBalance(entries: readonly BalanceEntry[], accountId: string): BalanceEntry | undefined {
  return entries
    .filter((entry) => entry.accountId === accountId)
    .reduce<BalanceEntry | undefined>(
      (best, entry) =>
        best === undefined || entry.asOf > best.asOf || (entry.asOf === best.asOf && entry.enteredAt > best.enteredAt)
          ? entry
          : best,
      undefined,
    );
}

/**
 * Parses a typed dollar amount ("$182,400.50", "182400") to a number, or
 * null when it is not one. Blank is null too: the caller decides whether a
 * blank means "unchanged" or "use the measured value".
 */
export function parseMoney(text: string): number | null {
  const cleaned = text.replace(/[$,\s]/g, '');
  if (cleaned === '' || !/^-?\d+(\.\d{0,2})?$/.test(cleaned)) return null;
  return Number(cleaned);
}

/** "35" (a percent typed by a person) → 0.35; null when blank or out of 0–100. */
export function parsePercent(text: string): number | null {
  const cleaned = text.replace(/[%\s]/g, '');
  if (cleaned === '' || !/^\d+(\.\d+)?$/.test(cleaned)) return null;
  const value = Number(cleaned);
  return value > 100 ? null : Math.round(value * 10) / 1000;
}

/** The Payroll panel's typed fields (dollar amounts and yyyy-mm-dd dates as text). */
export interface PayrollForm {
  readonly perRun: string;
  readonly perRunNote: string;
  readonly currentTeamSince: string;
  readonly commissions: string;
  readonly benefitsMonthly: string;
  readonly benefitsDay: string;
  readonly anchorDate: string;
}
export type PayrollErrors = Partial<Record<keyof PayrollForm, string>>;

/** The server's limit on the payroll source note (ASSUMPTION_LIMITS.maxLabel). */
export const PAYROLL_NOTE_MAX = 200;

const amountText = (value: number | null): string => (value === null ? '' : String(value));

export function payrollFormFrom(assumptions: Pick<CashAssumptions, 'payroll' | 'benefits'>): PayrollForm {
  return {
    perRun: amountText(assumptions.payroll.perRunExcludingCommissions),
    perRunNote: assumptions.payroll.perRunSourceNote,
    currentTeamSince: assumptions.payroll.currentTeamSince ? ymd(assumptions.payroll.currentTeamSince) : '',
    commissions: amountText(assumptions.payroll.commissionsPerPayday),
    benefitsMonthly: amountText(assumptions.benefits.monthly),
    benefitsDay: String(assumptions.benefits.dayOfMonth),
    anchorDate: assumptions.payroll.anchorDate ? ymd(assumptions.payroll.anchorDate) : '',
  };
}

/**
 * Turns the Payroll panel into the PUT body (payroll and benefits are each
 * replaced whole), or the per-field errors. Blank amounts and dates are null:
 * the forecast then uses what it measures.
 */
export function parsePayrollForm(form: PayrollForm): {
  readonly body: { readonly payroll: PayrollSettings; readonly benefits: BenefitsSettings };
  readonly errors: PayrollErrors;
} {
  const errors: PayrollErrors = {};
  const optionalMoney = (key: keyof PayrollForm, positive: boolean): number | null => {
    if (form[key].trim() === '') return null;
    const value = parseMoney(form[key]);
    if (value === null || value < 0 || (positive && value === 0)) {
      errors[key] = positive ? 'Enter a dollar amount above zero' : 'Enter a dollar amount';
      return null;
    }
    return value;
  };
  const day = Number(form.benefitsDay);
  if (!Number.isInteger(day) || day < 1 || day > 31) errors.benefitsDay = 'Enter a day from 1 to 31';
  if (form.perRunNote.trim().length > PAYROLL_NOTE_MAX) errors.perRunNote = `Keep it under ${PAYROLL_NOTE_MAX} characters`;
  return {
    body: {
      payroll: {
        perRunExcludingCommissions: optionalMoney('perRun', true),
        perRunSourceNote: form.perRunNote.trim(),
        currentTeamSince: form.currentTeamSince === '' ? null : form.currentTeamSince,
        commissionsPerPayday: optionalMoney('commissions', false),
        anchorDate: form.anchorDate === '' ? null : form.anchorDate,
      },
      benefits: { monthly: optionalMoney('benefitsMonthly', false), dayOfMonth: day },
    },
    errors,
  };
}

/** A calibrated number from the forecast's parameters (the measured payroll, say), if present. */
export function calibratedNumber(parameters: Readonly<Record<string, unknown>> | undefined, key: string): Calibrated<number> | undefined {
  const raw = parameters?.[key];
  return isCalibrated(raw) && typeof raw.value === 'number' ? { ...raw, value: raw.value } : undefined;
}

/** A calibrated date (ISO) from the forecast's parameters, if present. */
export function calibratedDate(parameters: Readonly<Record<string, unknown>> | undefined, key: string): Calibrated<string> | undefined {
  const raw = parameters?.[key];
  return isCalibrated(raw) && typeof raw.value === 'string' ? { ...raw, value: raw.value } : undefined;
}

/** A balance older than this is stale (the server's STALE_BALANCE_DAYS). */
export const STALE_BALANCE_DAYS = 7;
const DAY_MS = 86_400_000;

/**
 * Accounts whose balance is more than `days` old on `asOf`, or that have none:
 * the forecast is starting from an old number.
 */
export function staleAccounts(
  accounts: readonly OpeningAccount[],
  asOf: string,
  days: number = STALE_BALANCE_DAYS,
): OpeningAccount[] {
  const cutoff = Date.parse(`${ymd(asOf)}T00:00:00Z`) - days * DAY_MS;
  return accounts.filter((account) => account.asOf === null || Date.parse(`${ymd(account.asOf)}T00:00:00Z`) < cutoff);
}

/** The id for an account entered by hand (QuickBooks ids are numeric, so these never collide). */
export function manualAccountId(random: () => string): string {
  return `manual-${random().replace(/[^a-z0-9]/gi, '').slice(0, 12).toLowerCase()}`;
}

/** Loose shape check for an email typed into the recipients list; the server has the final say. */
export function looksLikeEmail(text: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text.trim());
}

/** QuickBooks calls a card account "Credit Card"; everything else the forecast tracks is a bank account. */
export function accountKindOf(account: Pick<QuickBooksAccount, 'accountType'>): AccountKind {
  return account.accountType === 'Credit Card' ? ACCOUNT_KIND.creditCard : ACCOUNT_KIND.bank;
}

// ─── Forecast vs actual ──────────────────────────────────────────────────────

/** One snapshot at the chosen horizon, flattened for the table. */
export interface AccuracyTableRow {
  readonly snapshotId: string;
  readonly weekStart: string;
  readonly weekText: string;
  readonly forecastIn: number;
  readonly actualIn: number;
  readonly inVariance: number;
  readonly forecastOut: number;
  readonly actualOut: number;
  readonly outVariance: number;
  readonly forecastClosing: number;
  readonly actualClosing: number | null;
  readonly closingVariance: number | null;
  readonly complete: boolean;
}

/** Picks each snapshot's comparison at one horizon; snapshots too young for it are left out. */
export function accuracyRows(rows: readonly AccuracyRow[], weeks: number): AccuracyTableRow[] {
  return rows.flatMap((row) => {
    const horizon = row.horizons.find((candidate) => candidate.weeks === weeks);
    if (!horizon) return [];
    return [
      {
        snapshotId: row.snapshotId,
        weekStart: row.weekStart,
        weekText: `Week of ${longDate(row.weekStart)}`,
        forecastIn: horizon.forecastIn,
        actualIn: horizon.actualIn,
        inVariance: horizon.inVariance,
        forecastOut: horizon.forecastOut,
        actualOut: horizon.actualOut,
        outVariance: horizon.outVariance,
        forecastClosing: horizon.forecastClosing,
        actualClosing: horizon.actualClosing,
        closingVariance: horizon.closingVariance,
        complete: horizon.complete,
      },
    ];
  });
}

/** Mean absolute error over finished rows, or null with none. */
export function meanAbsolute(values: readonly (number | null)[]): number | null {
  const present = values.filter((value): value is number => value !== null);
  if (present.length === 0) return null;
  return present.reduce((sum, value) => sum + Math.abs(value), 0) / present.length;
}
